import {
  CaseCategory,
  caseIsOpen,
  useCases,
  usePersonCaseCategories,
  usePersonCaseCategoriesById,
} from '~/entities/case'
import { differenceInDays, parseISO } from 'date-fns'
import { CaseOrCategory, CaseWithTitle, Grouped } from './types'
import { useMemo } from 'react'
import TriageCaseOrCategorySelect from './Select'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'

interface BaseProps {
  onChange: (value: CaseOrCategory) => void
  onBlur?: () => void
}

type Communication =
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object']

type ChatCommunication = Extract<Communication, { kind: 'chat' }>

type PhoneCommunication = Extract<Communication, { kind: 'phone' }>

type ChatOrPhoneCommunication = ChatCommunication | PhoneCommunication

interface Props extends BaseProps {
  message?: Pick<ChatOrPhoneCommunication, 'id'> & {
    case_relations: {
      case: Pick<ChatOrPhoneCommunication['case_relations'][number]['case'], 'id'>
    }[]
  }
  filterCarePlanCases?: boolean
  /**
   * When true (default), exclude cases already linked to the message.
   * For message forwarding workflow we want to include already-linked cases, so pass false.
   */
  excludeAlreadyLinkedCases?: boolean
  person: { id: number }
  threadUid: string | null
}

/**
 * Finds case options that are candidates for linking to a message
 */
export default function TriageCaseOrCategorySelectContainer(props: Props) {
  // Filtered list for triage category selection to create a new case
  const { result: categories = [] } = usePersonCaseCategories(props.person.id)
  // Full unfiltered list for existing cases
  const { result: allCaseCategoriesById = {} } = usePersonCaseCategoriesById(null)
  const { data: cases = [] } = useCases({ personId: props.person.id })

  const options = useMemo((): Grouped<CaseOrCategory>[] => {
    const linkedCases = props.message?.case_relations.map(relation => relation.case) ?? []

    // Build candidate cases, optionally excluding those already linked to this message
    const availableCases = cases.filter(caseObject => {
      // Is this message already linked to this case?
      const alreadyLinked = linkedCases.find(linkedCase => linkedCase.id === caseObject.id)
      if (alreadyLinked && (props.excludeAlreadyLinkedCases ?? true)) {
        return false
      }

      if (props.filterCarePlanCases) {
        if (caseObject.relations?.some(relation => relation.content_type === 'careplan')) {
          // Is it linked to a care plan?
          return false
        }
      }

      if (caseIsOpen(caseObject)) {
        return true
      }

      // Closed within the past day?
      if (differenceInDays(new Date(), parseISO(caseObject.updated_at)) <= 1) {
        return true
      }

      return false
    })

    // Sort by most recently updated and add titles
    const caseOptions: CaseWithTitle[] = availableCases
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .map(c => ({ ...c, title: allCaseCategoriesById[c.category]?.title || `Case ${c.id}` }))

    const recentCases: Grouped<CaseWithTitle>[] = caseOptions
      .slice(0, 3)
      .map(c => ({ ...c, group: 'Recently Updated' }))

    const otherOpenCases: Grouped<CaseWithTitle>[] = caseOptions
      .slice(3)
      .filter(c => caseIsOpen(c))
      .map(c => ({ ...c, group: 'Other Open Cases' }))

    const groupedCategories: Grouped<CaseCategory>[] = categories.map(c => ({
      ...c,
      group: c.parent || 'Uncategorized',
      title: '# ' + c.title,
    }))

    const allOptions = [...recentCases, ...otherOpenCases, ...groupedCategories]

    // Sort with priority mapping
    const priorityMap = {
      'Recently Updated': 0,
      'Other Open Cases': 1,
    } as Record<string, number | undefined>

    return allOptions.sort((a, b) => {
      const akey = a.group
      const bkey = b.group

      if (priorityMap[akey] !== undefined && priorityMap[bkey] !== undefined) {
        return priorityMap[akey] - priorityMap[bkey]
      } else if (priorityMap[akey] !== undefined) {
        return -1
      } else if (priorityMap[bkey] !== undefined) {
        return 1
      }

      return a.group.localeCompare(b.group)
    })
  }, [
    cases,
    categories,
    allCaseCategoriesById,
    props.message,
    props.filterCarePlanCases,
    props.excludeAlreadyLinkedCases,
  ])

  return (
    <TriageCaseOrCategorySelect options={options} onChange={props.onChange} onBlur={props.onBlur} />
  )
}
